import React, { useRef } from 'react';
import styled from 'styled-components';
import FolderIcon from '@mui/icons-material/Folder';

/**
 * Button component for uploading folders with their structure preserved
 *
 * Uses the webkitdirectory attribute to allow folder selection
 * Captures file paths relative to the selected folder
 */
const FolderUploadButton = ({ onFolderSelect, disabled = false }) => {
  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // System/hidden files to ignore
  const IGNORED_FILES = [
    '.DS_Store',
    '.ds_store',
    'Thumbs.db',
    'thumbs.db',
    'desktop.ini',
    '.gitignore',
    '.gitkeep',
    '.npmignore',
  ];

  const isIgnoredFile = (fileName) => {
    // Check exact matches
    if (IGNORED_FILES.includes(fileName)) return true;
    // Check hidden files (starting with .)
    if (fileName.startsWith('.')) return true;
    return false;
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) {
      return;
    }

    // Filter out system/hidden files and extract folder structure
    const filesWithPaths = files
      .filter(file => !isIgnoredFile(file.name))
      .map(file => ({
        file: file,
        path: file.webkitRelativePath || file.name,
        name: file.name,
        size: file.size,
        type: file.type,
      }));

    if (filesWithPaths.length === 0) {
      return;
    }

    // Group files by their folder structure
    const folderStructure = extractFolderStructure(filesWithPaths);

    onFolderSelect({
      files: filesWithPaths,
      folderStructure: folderStructure,
      totalFiles: filesWithPaths.length,
      totalSize: filesWithPaths.reduce((sum, f) => sum + f.size, 0),
    });

    // Reset input so same folder can be selected again
    event.target.value = '';
  };

  /**
   * Extract unique folder paths from file paths
   */
  const extractFolderStructure = (filesWithPaths) => {
    const folders = new Set();

    filesWithPaths.forEach(({ path }) => {
      const parts = path.split('/');

      // Remove filename (last part) to get folder path
      parts.pop();

      // Add all folder levels
      let currentPath = '';
      parts.forEach(part => {
        currentPath += (currentPath ? '/' : '') + part;
        folders.add(currentPath);
      });
    });

    return Array.from(folders).sort();
  };

  return (
    <Wrapper>
      <StyledButton
        onClick={handleButtonClick}
        disabled={disabled}
      >
        <FolderIcon style={{ marginRight: '8px', fontSize: '20px' }} />
        Select a folder to upload
      </StyledButton>
      <ButtonHint>All supported files and subfolders will be uploaded</ButtonHint>
      <input
        ref={fileInputRef}
        type="file"
        webkitdirectory="true"
        directory="true"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 12px 28px;
  background: none;
  color: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 4px;
  font-size: 15px;
  font-weight: 500;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};

  &:hover {
    background-color: ${({ theme, disabled }) => disabled ? 'transparent' : `${theme.colors.secondary}10`};
  }

  &:focus {
    outline: none;
  }
`;

const ButtonHint = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 6px 0 0;
`;

export default FolderUploadButton;
